import { useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import Modal from '../modal/Modal'
import { Button } from '../generic/Core'
import useTranslation from '../../hooks/useTranslation'

/**
 * The import dialog (task 35.02): pick a file, see what would change, approve
 * what you want, apply it.
 *
 * The dialog holds onto the File across both phases so apply can re-post the
 * same bytes without asking the user to pick it again, and so nothing has to be
 * parked server-side between them (§11.1).
 *
 * Updates approve per field, inserts approve whole (§11.4).
 */

/*
 * A target worth aiming at. A one-line dashed strip reads as a button and gets
 * used as one; a 3:2 panel reads as somewhere to put something, which is what
 * dragging a file needs.
 *
 * It collapses once there is a plan on screen: from then on the rows are what
 * the dialog is for, and the zone still accepts a drop without taking a third of
 * the height to say so.
 */
const Drop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  width: 100%;
  aspect-ratio: 3 / 2;
  max-height: 220px;

  border: 2px dashed ${({ theme, $active }) => $active ? theme.btn.primary : theme.form.border};
  background: ${({ theme, $active }) => $active ? theme.form.readOnlyBg : 'transparent'};
  border-radius: 0.5rem;
  text-align: center;
  cursor: pointer;
  color: ${({ theme }) => theme.font.label};
  font-size: var(--font-XS);
  transition: border-color 120ms ease, background 120ms ease;

  &:hover { border-color: ${({ theme }) => theme.btn.primary}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.btn.primary};
    outline-offset: 2px;
  }

  ${({ $compact }) => $compact && `
    aspect-ratio: auto;
    max-height: none;
    flex-direction: row;
    padding: 0.6rem 0.75rem;
    gap: 0.5rem;
  `}
`

/*
 * Everything inside the zone is inert to the drag events. Without this a drag
 * over the filename or the icon fires dragleave on the zone itself and the
 * highlight flickers off while the pointer is still inside it.
 */
const DropInner = styled.div`
  pointer-events: none;
  display: contents;
`

const DropTitle = styled.div`
  font-size: var(--font-S);
  color: ${({ theme }) => theme.font.color};
`

const DropHint = styled.div`
  font-size: var(--font-XS);
  color: ${({ theme }) => theme.font.label};
`

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0 0.75rem;
`

const Count = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: 0.5rem;
  font-size: var(--font-XS);
  background: ${({ theme }) => theme.form.readOnlyBg};
  color: ${({ theme }) => theme.font.color};
`

const Rows = styled.div`
  max-height: 45vh;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.form.border};
  border-radius: 0.5rem;
`

const Row = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.form.border};

  &:last-child { border-bottom: none; }
`

const RowHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--font-XS);
`

const Action = styled.span`
  padding: 0.1rem 0.45rem;
  border-radius: 0.35rem;
  font-size: var(--font-XXS);
  text-transform: uppercase;
  color: #fff;
  background: ${({ theme, $action }) => $action === 'insert' ? theme.emotive.success.color
        : $action === 'error' ? theme.btn.danger
            : theme.btn.primary};
`

const Change = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
  padding: 0.15rem 0 0.15rem 0.5rem;
  font-size: var(--font-XXS);
  color: ${({ theme }) => theme.font.label};
`

const From = styled.span`
  text-decoration: line-through;
  opacity: 0.75;
`

const To = styled.span`
  color: ${({ theme }) => theme.font.color};
  font-weight: 600;
`

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: var(--font-XS);
  color: ${({ theme }) => theme.font.label};
`

/** An insert approves as one decision, so its field set is this sentinel (§11.4). */
const ALL = 'ALL'

const Check = styled.input.attrs({ type: 'checkbox' })`
  margin: 0 0.15rem 0 0;
  cursor: pointer;
`

/**
 * Values are already typed; this is only about rendering them.
 *
 * A nested value — an invoice's lines — is summarised rather than printed. It
 * has no element identity to diff against (see importData), so the honest thing
 * to show is that the whole array is being replaced, and how big it is.
 */
const show = (value, t) => {
    if (value === null || value === undefined || value === '') return '—'
    if (Array.isArray(value)) return `${value.length} ${t('import.modal.items')}`
    if (typeof value === 'object') return '{…}'
    return String(value)
}

const ImportModal = ({ onClose, show: isShown = true, zIndex, onPlan, onApply, isPlanning, isApplying, importFormats = [], fieldLabel = (field) => field }) => {
    const { t, m } = useTranslation()
    const inputRef = useRef(null)

    // Built from the format registry, so adding an importable format widens the
    // picker without touching this file.
    const accept = importFormats.map(format => `.${format.extension}`).join(',')
    const extensions = importFormats.map(format => format.extension?.toLowerCase()).filter(Boolean)

    const [file, setFile] = useState(null)
    const [plan, setPlan] = useState(null)
    const [report, setReport] = useState(null)
    // Set instead of `report` when the apply went to the queue (35.02 M4).
    const [queued, setQueued] = useState(null)
    const [error, setError] = useState(null)
    // Row number -> Set of approved field names. Insert rows hold the sentinel
    // ALL, because a new record is one decision rather than several.
    const [approved, setApproved] = useState(new Map())

    // Nested elements fire dragenter/dragleave of their own as the pointer
    // crosses them. Counting entries against leaves is what makes the highlight
    // track the zone rather than whatever is under the cursor.
    const dragDepth = useRef(0)
    const [dragging, setDragging] = useState(false)

    const take = (picked) => {
        if (!picked) return

        /*
         * The file picker enforces `accept`; a drop does not — the OS will hand
         * over a .docx as happily as a .csv. Checked against the same registry
         * the picker is built from, so the two cannot disagree.
         */
        const extension = picked.name.split('.').pop()?.toLowerCase()
        if (extensions.length > 0 && !extensions.includes(extension)) {
            setError('import.error.wrongExtension')
            return
        }

        setFile(picked)
        setPlan(null)
        setReport(null)
        setQueued(null)
        setError(null)
        setApproved(new Map())
    }

    const handlePick = (event) => {
        take(event.target.files?.[0])
        // Cleared so that picking the same file twice in a row still fires
        // onChange — after an apply, re-comparing the same file is ordinary.
        event.target.value = ''
    }

    const handleDragOver = (event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
    }

    const handleDragEnter = (event) => {
        event.preventDefault()
        dragDepth.current += 1
        setDragging(true)
    }

    const handleDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragging(false)
    }

    const handleDrop = (event) => {
        event.preventDefault()
        dragDepth.current = 0
        setDragging(false)
        // One file: an import is one file, and silently taking the first of five
        // would be worse than taking none.
        const dropped = event.dataTransfer?.files
        if (dropped?.length === 1) take(dropped[0])
        else if (dropped?.length > 1) setError('import.error.oneFile')
    }

    const handlePlan = async () => {
        const result = await onPlan({ file })
        if (result?.error) {
            setError(result.error.error || 'import.error.failed')
            setPlan(null)
            return
        }
        setError(null)
        setReport(null)
        setQueued(null)
        setPlan(result.plan)
        // Everything actionable starts approved; the user unticks what they do
        // not want. Rows carrying errors are never pre-approved.
        setApproved(new Map(
            result.plan.rows
                .filter(row => row.action !== 'error')
                .map(row => [row.row, row.action === 'insert' ? ALL : new Set(row.changes.map(c => c.field))])
        ))
    }

    const toggleRow = (row) => setApproved(prev => {
        const next = new Map(prev)
        if (next.has(row.row)) next.delete(row.row)
        else next.set(row.row, row.action === 'insert' ? ALL : new Set(row.changes.map(c => c.field)))
        return next
    })

    const toggleField = (row, field) => setApproved(prev => {
        const next = new Map(prev)
        const current = next.get(row.row)
        const fields = current === ALL || !current ? new Set() : new Set(current)

        if (fields.has(field)) fields.delete(field)
        else fields.add(field)

        if (fields.size === 0) next.delete(row.row)
        else next.set(row.row, fields)
        return next
    })

    const isRowApproved = (row) => approved.has(row.row)
    const isFieldApproved = (row, field) => {
        const current = approved.get(row.row)
        return current === ALL ? true : Boolean(current?.has(field))
    }

    const approvals = plan?.rows
        .filter(row => row.action !== 'error' && approved.has(row.row))
        .map(row => {
            const current = approved.get(row.row)
            const fields = current === ALL ? undefined : [...current]
            return {
                row: row.row,
                ...(fields ? { fields } : {}),
                fingerprints: Object.fromEntries((row.changes || [])
                    .filter(change => change.fingerprint)
                    .map(change => [change.field, change.fingerprint]))
            }
        }) || []

    const approvedCount = approvals.reduce(
        (total, approval) => total + (approval.fields ? approval.fields.length : 1), 0)

    /*
     * Past this many approved rows the apply runs in the background and answers
     * with a notification instead of a report (35.02 M4). The plan reports the
     * threshold so the dialog can say which of the two is about to happen while
     * the user is still choosing. It is a notice, not a limit — nothing is
     * refused for being large.
     */
    const syncRows = plan?.syncApplyRows
    const willQueue = Boolean(syncRows && approvals.length > syncRows)

    const handleApply = async () => {
        const result = await onApply({ file, approvals })
        if (result?.error) {
            setError(result.error.error || 'import.error.failed')
            return
        }
        setError(null)
        setPlan(null)
        // A queued apply has no report to show: it answers over SSE once the
        // writes land. Keep the dialog on a state that says so rather than
        // rendering an empty report.
        if (result.report?.queued) setQueued(result.report)
        else setReport(result.report)
    }

    const summary = plan?.summary

    return ReactDOM.createPortal(
        <Modal zIndex={zIndex} show={isShown} onClose={onClose} setShow={onClose}>
            <Modal.Header onClose={onClose}>
                <h4>{t('import.modal.title')}</h4>
            </Modal.Header>
            <Modal.Body>
                <Drop
                    $active={dragging}
                    $compact={Boolean(plan || report || queued)}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                    aria-label={t('import.modal.drop')}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            inputRef.current?.click()
                        }
                    }}
                >
                    <DropInner>
                        <DropTitle>{file ? file.name : t('import.modal.drop')}</DropTitle>
                        <DropHint>
                            {file
                                ? t('import.modal.replace')
                                : m('import.modal.accepts', [extensions.join(', ').toUpperCase()])}
                        </DropHint>
                    </DropInner>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        style={{ display: 'none' }}
                        onChange={handlePick}
                    />
                </Drop>

                {error && <Empty>{t(error)}</Empty>}

                {summary && (
                    <>
                        <Summary>
                            <Count>{t('import.modal.insert')}: {summary.insert}</Count>
                            <Count>{t('import.modal.update')}: {summary.update}</Count>
                            <Count>{t('import.modal.unchanged')}: {summary.unchanged}</Count>
                            {summary.error > 0 && <Count>{t('import.modal.error')}: {summary.error}</Count>}
                                {plan.localeFromFile && (
                                <Count>{t('import.modal.readAs')} {plan.locale}</Count>
                            )}
                            {willQueue && (
                                <Count>{t('import.modal.willQueue')}</Count>
                            )}
                            {summary.blockedColumns?.length > 0 && (
                                <Count>
                                    {t('import.modal.blockedColumns')}:{' '}
                                    {summary.blockedColumns.map(c => fieldLabel(c.field)).join(', ')}
                                </Count>
                            )}
                            {summary.skippedColumns?.length > 0 && (
                                <Count>{t('import.modal.skippedColumns')}: {summary.skippedColumns.join(', ')}</Count>
                            )}
                        </Summary>

                        <Rows>
                            {plan.rows.length === 0 && <Empty>{t('import.modal.noChanges')}</Empty>}
                            {plan.rows.map(row => (
                                <Row key={row.row}>
                                    <RowHead>
                                        {row.action !== 'error' && (
                                            <Check
                                                checked={isRowApproved(row)}
                                                onChange={() => toggleRow(row)}
                                            />
                                        )}
                                        <Action $action={row.action}>{t(`import.action.${row.action}`)}</Action>
                                        <span>{row.label}</span>
                                    </RowHead>

                                    {row.action === 'error' && <Change>{t(row.error)}</Change>}

                                    {row.changes?.map(change => (
                                        <Change key={change.field}>
                                            {/* Updates approve per field; an insert is one decision (§11.4). */}
                                            {row.action === 'update' && (
                                                <Check
                                                    checked={isFieldApproved(row, change.field)}
                                                    onChange={() => toggleField(row, change.field)}
                                                />
                                            )}
                                            <span>{fieldLabel(change.field)}</span>
                                            {row.action === 'update' && <From>{show(change.from, t)}</From>}
                                            <To>{show(change.to, t)}</To>
                                        </Change>
                                    ))}

                                    {row.blocked?.map(blockedField => (
                                        <Change key={blockedField.field}>
                                            <span>{fieldLabel(blockedField.field)}</span>
                                            <span>{t(`import.blocked.${blockedField.reason}`)}</span>
                                        </Change>
                                    ))}
                                </Row>
                            ))}
                        </Rows>
                    </>
                )}
                {queued && (
                    <Summary>
                        <Count>{m('import.report.queued', [queued.count])}</Count>
                    </Summary>
                )}
                {report && (
                    <Summary>
                        <Count>{t('import.report.applied')}: {report.summary.applied}</Count>
                        {report.summary.conflicts > 0 && <Count>{t('import.report.conflicts')}: {report.summary.conflicts}</Count>}
                        {report.summary.failed > 0 && <Count>{t('import.report.failed')}: {report.summary.failed}</Count>}
                        {report.summary.duplicates > 0 && <Count>{t('import.report.duplicates')}: {report.summary.duplicates}</Count>}
                        {report.summary.skipped > 0 && <Count>{t('import.report.skipped')}: {report.summary.skipped}</Count>}
                    </Summary>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" $variant="secondary" onClick={onClose}>
                    {report || queued ? t('all.confirm.close.accept') : t('all.confirm.cancel')}
                </Button>

                {!plan && !report && !queued && (
                    <Button type="button" $variant="primary" disabled={!file || isPlanning} onClick={handlePlan}>
                        {isPlanning ? t('import.modal.working') : t('import.modal.accept')}
                    </Button>
                )}

                {plan && (
                    <Button type="button" $variant="primary" disabled={approvedCount === 0 || isApplying} onClick={handleApply}>
                        {isApplying ? t('import.modal.applying') : `${t('import.modal.apply')} (${approvedCount})`}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>,
        document.body
    )
}

export default ImportModal
